/**
 * Round-trip handoff: Hyper Dusk → PhotoEdit → back into the same project.
 *
 * Mirrors the Tours↔Staging pattern:
 *   1. Open PhotoEdit with imageUrl + returnTo=hyperdusk + project/image ids
 *   2. PhotoEdit posts a message (and/or deep-links back with editedImageUrl)
 *   3. Hyper Dusk appends the result as a dusk variant
 */

import { supabase } from './supabase';

const PHOTOEDIT_APP_URL = (
  (import.meta as { env?: { VITE_PHOTOEDIT_APP_URL?: string } }).env?.VITE_PHOTOEDIT_APP_URL ?? ''
)
  .toString()
  .trim() || 'https://photoedit.rehub.studio';

const ALLOWED_RETURN_ORIGINS = new Set<string>([
  PHOTOEDIT_APP_URL.replace(/\/$/, ''),
  'https://photoedit.rehub.studio',
  'http://localhost:5178',
]);

export const PHOTOEDIT_RETURN_MESSAGE = 'photoedit-hyperdusk-return';

export interface PhotoEditReturnPayload {
  type: typeof PHOTOEDIT_RETURN_MESSAGE;
  projectId: string;
  imageId: string;
  imageUrl: string;
}

export function openPhotoEditHandoff(args: {
  imageUrl: string;
  projectId: string;
  imageId: string;
}): Window | null {
  const returnOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const params = new URLSearchParams({
    imageUrl: args.imageUrl,
    source: 'hyperdusk',
    returnTo: 'hyperdusk',
    projectId: args.projectId,
    imageId: args.imageId,
    returnOrigin,
  });
  const baseUrl = `${PHOTOEDIT_APP_URL.replace(/\/$/, '')}/?${params.toString()}`;

  // Open sync to keep the click-grant for popup blockers.
  const win = window.open('about:blank', '_blank');

  void (async () => {
    let hash = '';
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token && session?.refresh_token) {
        hash =
          `#sso_access_token=${encodeURIComponent(session.access_token)}` +
          `&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
      }
    } catch {
      // Fall through without SSO.
    }
    const fullUrl = `${baseUrl}${hash}`;
    if (win && !win.closed) {
      win.location.href = fullUrl;
    } else {
      window.location.href = fullUrl;
    }
  })();

  return win;
}

export function installPhotoEditReturnListener(
  onReturn: (payload: PhotoEditReturnPayload) => void
): () => void {
  const handler = (event: MessageEvent) => {
    if (!ALLOWED_RETURN_ORIGINS.has(event.origin)) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;
    const msg = data as Partial<PhotoEditReturnPayload>;
    if (msg.type !== PHOTOEDIT_RETURN_MESSAGE) return;
    if (
      typeof msg.projectId !== 'string' ||
      typeof msg.imageId !== 'string' ||
      typeof msg.imageUrl !== 'string'
    ) {
      return;
    }
    if (!/^https?:\/\//i.test(msg.imageUrl)) return;
    onReturn(msg as PhotoEditReturnPayload);
  };
  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}

export interface EditedImageHandoff {
  projectId: string;
  imageId: string;
  imageUrl: string;
}

/** Parse inbound `?editedImageUrl=` deep link from PhotoEdit (tab closed / opener lost). */
export function readEditedImageHandoff(): EditedImageHandoff | null {
  const params = new URLSearchParams(window.location.search);
  const imageUrl = params.get('editedImageUrl');
  const imageId = params.get('imageId');
  const pathMatch = window.location.pathname.match(/^\/p\/([0-9a-fA-F-]+)$/);
  const projectId = params.get('projectId') || pathMatch?.[1] || null;
  if (!imageUrl || !imageId || !projectId) return null;
  if (!/^https?:\/\//i.test(imageUrl)) return null;
  if (!/^[0-9a-f-]{36}$/i.test(imageId) || !/^[0-9a-f-]{36}$/i.test(projectId)) return null;
  return { projectId, imageId, imageUrl };
}

let pendingEditedHandoff: EditedImageHandoff | null = null;

export function setPendingEditedHandoff(handoff: EditedImageHandoff | null): void {
  pendingEditedHandoff = handoff;
}

/** Consume a deep-link return so ProjectDetail can import it after mount. */
export function takePendingEditedHandoff(projectId: string): EditedImageHandoff | null {
  if (!pendingEditedHandoff || pendingEditedHandoff.projectId !== projectId) return null;
  const next = pendingEditedHandoff;
  pendingEditedHandoff = null;
  return next;
}
